/**
 * SalesManagerList Component
 *
 * Sortable list of sales managers with expandable rows.
 * Includes column headers with sort indicators.
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { SalesManager, SortConfig, SortField } from './types';
import { SalesManagerRow } from './SalesManagerRow';

interface SalesManagerListProps {
  managers: SalesManager[];
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

/**
 * Sort column configuration
 */
const SORT_COLUMNS: Array<{
  field: SortField;
  label: string;
  shortLabel: string;
  align: 'left' | 'right';
}> = [
  { field: 'name', label: 'Manager', shortLabel: 'Name', align: 'left' },
  { field: 'totalGross', label: 'Total Gross', shortLabel: 'Gross', align: 'right' },
  { field: 'totalUnits', label: 'Units', shortLabel: 'Units', align: 'right' },
];

export const SalesManagerList: React.FC<SalesManagerListProps> = ({
  managers,
  sortConfig,
  onSortChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((managerId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(managerId)) {
        next.delete(managerId);
      } else {
        next.add(managerId);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-blue-600" />
    );
  };

  if (managers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No sales manager data available for this period.
      </div>
    );
  }

  return (
    <div>
      {/* Sort header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Sort by:</span>
          {SORT_COLUMNS.map((col) => (
            <button
              key={col.field}
              onClick={() => handleSort(col.field)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                sortConfig.field === col.field
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <span>{col.shortLabel}</span>
              {renderSortIndicator(col.field)}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {managers.length} manager{managers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Manager rows */}
      <div className="bg-white rounded-b-lg">
        {managers.map((manager, index) => (
          <SalesManagerRow
            key={manager.managerId}
            manager={manager}
            isExpanded={expandedIds.has(manager.managerId)}
            onToggle={() => toggleExpanded(manager.managerId)}
            rank={index + 1}
          />
        ))}
      </div>
    </div>
  );
};
